import { useEffect, useMemo, useState } from "react";
import { Search, X, Command } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export const CommandPalette = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((v) => !v); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user]);

  useEffect(() => {
    if (open) {
      api.toolbox().then((d) => {
        const flat = [];
        (d.modes || []).forEach((m) => (m.sections || []).forEach((s) => (s.items || []).forEach((it) =>
          flat.push({ mode: m.title, section: s.title, ...it }))));
        setItems(flat);
      }).catch(() => {});
    }
  }, [open]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items.slice(0, 8);
    return items.filter((it) => `${it.mode} ${it.section} ${it.t} ${it.st} ${it.body}`.toLowerCase().includes(term)).slice(0, 12);
  }, [q, items]);

  if (!user || !open) return null;

  return (
    <div data-testid="command-palette" className="fixed inset-0 z-[70] bg-[#0B1B33]/60 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={() => setOpen(false)}>
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#012141]/10">
          <Search size={18} className="text-slate-400" />
          <input data-testid="palette-search" autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }} placeholder="Chercher un script, une objection, un sujet…" className="flex-1 text-sm focus:outline-none" />
          <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {results.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Aucun résultat.</p>}
          {results.map((it, i) => (
            <button key={i} data-testid={`palette-result-${i}`} onClick={() => setSel(sel === i ? null : i)} className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-[#012141]/5">
              <div className="flex items-center gap-2 text-[11px] text-[#C89A3E] font-semibold uppercase tracking-wide">{it.mode} · {it.section}</div>
              <div className="text-sm font-semibold text-[#012141]">{it.t}</div>
              {sel === i && it.body && <div className="mt-1.5 text-sm text-slate-600 whitespace-pre-line">{it.body}</div>}
            </button>
          ))}
        </div>
        <div className="px-4 py-2 border-t border-[#012141]/10 text-[11px] text-slate-400 flex items-center gap-1"><Command size={12} /> Astuce : Cmd/Ctrl + K pour ouvrir cette recherche partout.</div>
      </div>
    </div>
  );
};
